import React, { useState, useEffect } from 'react';
import { Activity, RefreshCw, User, LogOut, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import careMonitorService from '../services/careMonitorService';
import PatientVitalsModal from '../components/PatientVitalsModal';
import toast from 'react-hot-toast';
import { doctorService } from '../services/doctorService';
import clinicianService from '../services/clinicianService';

const ACVitalsMonitor = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');

  // Check permission on page load
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        
        // Allow admin users (superusers)
        if (user.is_superuser) {
          return true;
        }
        
        // Check for doctor or clinician permission
        if (user.is_doctor) {
          const doctor = await doctorService.getDoctorByUserId(user.id);
          const accessCheck = await doctorService.checkAcVitalsAccess(doctor.id);
          
          if (!accessCheck.has_access) {
            setAccessDenied(true);
            setAccessMessage(accessCheck.reason || 'You do not have permission to access AC Vitals Monitor');
            toast.error('Access denied: ' + (accessCheck.reason || 'No permission'));
            setTimeout(() => navigate('/doctor/dashboard'), 2000);
            return false;
          }
          return true;
        } else if (user.is_clinician) {
          const clinician = await clinicianService.getClinicianByUserId(user.id);
          const accessCheck = await clinicianService.checkAcVitalsAccess(clinician.id);
          
          if (!accessCheck.has_access) {
            setAccessDenied(true);
            setAccessMessage(accessCheck.reason || 'You do not have permission to access AC Vitals Monitor');
            toast.error('Access denied: ' + (accessCheck.reason || 'No permission'));
            setTimeout(() => navigate('/clinician/dashboard'), 2000);
            return false;
          }
          return true;
        }
        
        // If no valid role, deny access
        setAccessDenied(true);
        setAccessMessage('Invalid user role');
        toast.error('Access denied: Invalid user role');
        setTimeout(() => navigate('/auth/login'), 2000);
        return false;
      } catch (error) {
        console.error('Error checking AC vitals access:', error);
        setAccessDenied(true);
        setAccessMessage('Error verifying access permissions');
        toast.error('Failed to verify access permissions');
        return false;
      }
    };
    
    checkPermission().then(hasAccess => {
      if (hasAccess) {
        loadPatients();
      }
    });
    
    document.title = 'MediFusion - AC Vitals Monitor';
    
    // Auto-refresh every 30 seconds (only if has access)
    const interval = setInterval(() => {
      if (!accessDenied) {
        loadPatients(true);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [navigate, accessDenied]);

  const loadPatients = async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) {
        setLoading(patients.length === 0);
        setRefreshing(patients.length > 0);
      }
      
      // Fetch REAL aged care patients with today's vitals from backend
      const data = await careMonitorService.getAgedCarePatients();
      
      // Extract patients array from response
      const patientsData = data.patients || [];
      
      // Transform backend data to match UI format
      const transformedPatients = patientsData.map(patient => ({
        id: patient.id, // Numeric database ID for fetching detailed vitals in modal
        patient_id: patient.patient_id,
        patient_name: patient.patient_name,
        age: patient.age,
        overall_color: patient.overall_color, // green, orange, red
        total_readings_today: patient.total_readings_today || 0,
        systolic_bp: patient.systolic_bp || null,
        diastolic_bp: patient.diastolic_bp || null,
        pulse_rate: patient.pulse_rate || null,
        temperature: patient.temperature || null,
        spo2: patient.spo2 || null,
        respiratory_rate: patient.respiratory_rate || null,
        glucose: patient.glucose || null
      }));
      
      setPatients(transformedPatients);
      setLastUpdated(new Date());
      
      console.log(`✅ Loaded ${transformedPatients.length} real aged care patients with vitals`);
      
    } catch (error) {
      console.error('Error loading aged care patients:', error);
      if (!isAutoRefresh) {
        toast.error('Failed to load patient data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const getCardHeaderClass = (overallColor) => {
    const colorMap = {
      'red': 'bg-red-500',
      'orange': 'bg-orange-500',
      'green': 'bg-green-500',
      'gray': 'bg-gray-400'
    };
    return colorMap[overallColor] || 'bg-gray-400';
  };

  const getVitalStatusClass = (vital) => {
    if (!vital || !vital.color) return 'text-gray-500';
    
    const statusMap = {
      'red': 'text-red-600 font-bold',
      'orange': 'text-orange-600 font-semibold',
      'green': 'text-green-600',
      'gray': 'text-gray-500'
    };
    return statusMap[vital.color] || 'text-gray-500';
  };

  const formatVitalValue = (vital) => {
    if (!vital || vital.value === null || vital.value === undefined) {
      return '--';
    }
    return `${vital.value}${vital.unit || ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading AC Vitals Monitor...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 rounded-lg p-8">
            <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-700 mb-4">{accessMessage}</p>
            <p className="text-sm text-red-600">You will be redirected to your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Header with Logos and Refresh */}
      <div className="bg-blue-600 text-white py-2 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Left Logo */}
            <div className="bg-white rounded px-2 py-1">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-xl font-bold">AC Vitals Monitor</h1>
          </div>
          <div className="flex items-center space-x-3">
            {/* Refresh Button */}
            <button
              onClick={() => loadPatients()}
              disabled={refreshing}
              className="flex items-center space-x-2 px-3 py-1.5 bg-white text-blue-600 rounded hover:bg-blue-50 disabled:bg-gray-300 disabled:text-gray-500 transition-colors text-sm font-medium"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
            {/* Right Logo */}
            <div className="bg-white rounded px-2 py-1">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Compact Status Info */}
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-blue-600" />
              <p className="text-blue-800 text-sm">
                Monitoring <span className="font-semibold">{patients.length}</span> aged care patient{patients.length !== 1 ? 's' : ''} with vitals recorded today
              </p>
            </div>
            <p className="text-blue-600 text-xs">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          </div>
        </div>

        {/* Patient Cards Grid - 5 per row */}
        {patients.length === 0 ? (
          <div className="text-center py-12">
            <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Patients Found</h3>
            <p className="text-gray-500">
              No aged care patients with vitals recorded today.
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Vitals will appear here when aged care patients have recordings for today.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {patients.map((patient) => (
              <div
                key={patient.patient_id}
                onClick={() => setSelectedPatient(patient)}
                className="bg-white rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:shadow-xl hover:scale-105 overflow-hidden"
              >
                {/* Compact Card Header - Color Coded (No Age) */}
                <div className={`${getCardHeaderClass(patient.overall_color)} text-white px-3 py-2`}>
                  <h3 className="font-bold text-base">{patient.patient_id}</h3>
                  <p className="text-xs opacity-90">{patient.patient_name}</p>
                </div>

                {/* Card Body - Latest Vitals */}
                <div className="p-4 space-y-2">
                  {/* Blood Pressure */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">BP:</span>
                    <span className={getVitalStatusClass(patient.systolic_bp)}>
                      {formatVitalValue(patient.systolic_bp)}/{formatVitalValue(patient.diastolic_bp)}
                    </span>
                  </div>

                  {/* Heart Rate */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">HR:</span>
                    <span className={getVitalStatusClass(patient.pulse_rate)}>
                      {formatVitalValue(patient.pulse_rate)}
                    </span>
                  </div>

                  {/* Temperature */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Temp:</span>
                    <span className={getVitalStatusClass(patient.temperature)}>
                      {formatVitalValue(patient.temperature)}
                    </span>
                  </div>

                  {/* SpO2 */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">SpO2:</span>
                    <span className={getVitalStatusClass(patient.spo2)}>
                      {formatVitalValue(patient.spo2)}
                    </span>
                  </div>

                  {/* Respiratory Rate */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">RR:</span>
                    <span className={getVitalStatusClass(patient.respiratory_rate)}>
                      {formatVitalValue(patient.respiratory_rate)}
                    </span>
                  </div>

                  {/* Glucose */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Glucose:</span>
                    <span className={getVitalStatusClass(patient.glucose)}>
                      {formatVitalValue(patient.glucose)}
                    </span>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
                  {patient.total_readings_today} reading{patient.total_readings_today !== 1 ? 's' : ''} today
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Patient Vitals Modal */}
      {selectedPatient && (
        <PatientVitalsModal
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
        />
      )}
    </div>
  );
};

export default ACVitalsMonitor;

